import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'sonner';
import {
  Award,
  BadgeCheck,
  BriefcaseBusiness,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Code2,
  ExternalLink,
  FileText,
  Gauge,
  Lock,
  MonitorCheck,
  PlayCircle,
  Send,
  Upload,
  Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { storage } from '../lib/firebase';
import {
  getAllCareerLaunchProfiles,
  getAllModules,
  getCareerLaunchProfile,
  getUserProgress,
  publishTalentReport,
  reviewCareerLaunchProfile,
  saveCareerLaunchProfile
} from '../lib/firestore';

const tabs = [
  'Hiring Readiness',
  'Portfolio Review',
  'Technical Quiz',
  'Practical Challenges',
  'Graduation Tasks',
  'Mock Interviews',
  'Take-Home Assignment',
  'System Design',
  'Behavioral Training',
  'Employer Report'
];

const scoreFields = [
  { key: 'resumeScore', label: 'Resume Quality' },
  { key: 'portfolioScore', label: 'Portfolio Strength' },
  { key: 'linuxBashScore', label: 'Linux & Bash' },
  { key: 'gitGithubScore', label: 'Git & GitHub' },
  { key: 'cloudIacScore', label: 'Cloud & IaC' },
  { key: 'dockerKubernetesScore', label: 'Docker & Kubernetes' },
  { key: 'cicdScore', label: 'CI/CD' },
  { key: 'observabilityScore', label: 'Observability' },
  { key: 'technicalQuizScore', label: 'Technical Quiz' },
  { key: 'practicalChallengeScore', label: 'Troubleshooting' },
  { key: 'mockInterviewScore', label: 'Communication' },
  { key: 'systemDesignScore', label: 'System Design' },
  { key: 'takeHomeScore', label: 'Take-Home Assignment' }
];

const challengeCards = [
  ['Broken Nginx Deployment', 'Restore a failed web server and explain the failure path.'],
  ['CI/CD Pipeline Failure', 'Fix a failing GitHub Actions workflow and document the root cause.'],
  ['Kubernetes Pod Crash', 'Investigate CrashLoopBackOff using logs, events, and manifests.'],
  ['Terraform Infrastructure Error', 'Resolve variable and state issues blocking deployment.'],
  ['Monitoring Failure', 'Repair Prometheus and Grafana integration so metrics appear.']
];

const quizTopics = ['Linux', 'Bash', 'Git', 'Cloud', 'Terraform', 'Docker', 'Kubernetes', 'CI/CD', 'Monitoring'];

const quizQuestions = [
  { topic: 'Linux', type: 'mcq', question: 'Which command shows running processes with CPU and memory usage?', options: ['top', 'scp', 'chmod', 'curl'], answer: 'top' },
  { topic: 'Linux', type: 'mcq', question: 'Which permission value gives read, write, and execute to the owner only?', options: ['700', '644', '755', '600'], answer: '700' },
  { topic: 'Linux', type: 'short', question: 'A disk is full on a production server. List the first three commands you would run and why.' },
  { topic: 'Bash', type: 'mcq', question: 'What does set -e do in a shell script?', options: ['Stops after a command fails', 'Prints every command', 'Exports variables', 'Runs in background'], answer: 'Stops after a command fails' },
  { topic: 'Bash', type: 'short', question: 'Write a one-line command to find the largest files in /var/log.' },
  { topic: 'Git', type: 'mcq', question: 'Which command creates a new branch and switches to it?', options: ['git checkout -b feature', 'git branch -d feature', 'git merge feature', 'git fetch feature'], answer: 'git checkout -b feature' },
  { topic: 'Git', type: 'short', question: 'Explain how you would recover after committing a secret to a public repository.' },
  { topic: 'Cloud', type: 'mcq', question: 'Which service type is best for running containers without managing virtual machines directly?', options: ['Managed container service', 'Block storage', 'DNS zone', 'Object archive'], answer: 'Managed container service' },
  { topic: 'Cloud', type: 'short', question: 'Describe how you would design a secure environment for a public web app and private database.' },
  { topic: 'Terraform', type: 'mcq', question: 'Which Terraform command previews infrastructure changes?', options: ['terraform plan', 'terraform fmt', 'terraform login', 'terraform output'], answer: 'terraform plan' },
  { topic: 'Terraform', type: 'mcq', question: 'What is Terraform state used for?', options: ['Tracking managed resources', 'Compiling code', 'Storing Docker images', 'Rotating SSH keys'], answer: 'Tracking managed resources' },
  { topic: 'Terraform', type: 'short', question: 'A teammate changed cloud resources manually. How would you detect and handle drift?' },
  { topic: 'Docker', type: 'mcq', question: 'Which file commonly defines instructions for building a container image?', options: ['Dockerfile', 'compose.lock', 'container.yml', 'Imagefile'], answer: 'Dockerfile' },
  { topic: 'Docker', type: 'mcq', question: 'Which command lists running containers?', options: ['docker ps', 'docker images', 'docker build', 'docker tag'], answer: 'docker ps' },
  { topic: 'Docker', type: 'short', question: 'Explain the difference between an image and a container.' },
  { topic: 'Kubernetes', type: 'mcq', question: 'What Kubernetes object keeps a desired number of pod replicas running?', options: ['Deployment', 'Secret', 'ConfigMap', 'IngressClass'], answer: 'Deployment' },
  { topic: 'Kubernetes', type: 'mcq', question: 'Which command shows pod events and scheduling details?', options: ['kubectl describe pod', 'kubectl cp', 'kubectl version --client', 'kubectl config use-context'], answer: 'kubectl describe pod' },
  { topic: 'Kubernetes', type: 'short', question: 'A pod is in CrashLoopBackOff. Walk through your troubleshooting steps.' },
  { topic: 'CI/CD', type: 'mcq', question: 'Which pipeline stage should normally fail when tests fail?', options: ['Continuous integration', 'DNS resolution', 'Artifact cleanup', 'Manual approval only'], answer: 'Continuous integration' },
  { topic: 'CI/CD', type: 'short', question: 'Describe a safe deployment strategy for releasing a new version with minimal downtime.' },
  { topic: 'Monitoring', type: 'mcq', question: 'Which signal is most directly related to user-facing request speed?', options: ['Latency', 'Repository stars', 'Image size alone', 'Number of branches'], answer: 'Latency' },
  { topic: 'Monitoring', type: 'short', question: 'What metrics and alerts would you configure for a Kubernetes web API?' },
  { topic: 'Security', type: 'mcq', question: 'Where should production secrets be stored?', options: ['A secrets manager', 'In source code', 'In a public README', 'In a Dockerfile comment'], answer: 'A secrets manager' },
  { topic: 'Security', type: 'short', question: 'Explain least privilege using an example from a DevOps workflow.' },
  { topic: 'Networking', type: 'mcq', question: 'What does DNS primarily provide?', options: ['Name to address resolution', 'CPU scheduling', 'Log rotation', 'Container image signing'], answer: 'Name to address resolution' },
  { topic: 'Networking', type: 'short', question: 'A service works by IP but not by domain name. What would you check?' },
  { topic: 'Incident Response', type: 'mcq', question: 'What should you do first during a production incident?', options: ['Assess impact and stabilize service', 'Delete all logs', 'Rewrite the app', 'Ignore alerts'], answer: 'Assess impact and stabilize service' },
  { topic: 'Incident Response', type: 'short', question: 'Write a short incident update for stakeholders when a deployment causes errors.' },
  { topic: 'DevOps Practice', type: 'mcq', question: 'What is the main benefit of infrastructure as code?', options: ['Repeatable, reviewable infrastructure changes', 'No need for monitoring', 'Manual server changes', 'Avoiding version control'], answer: 'Repeatable, reviewable infrastructure changes' },
  { topic: 'DevOps Practice', type: 'short', question: 'Describe one project from your portfolio using problem, action, and result.' }
];

const interviewRounds = [
  ['Linux and Bash', ['How do you check CPU, memory, and disk usage?', 'How would you troubleshoot a failing systemd service?']],
  ['Cloud and Infrastructure', ['What is infrastructure as code?', 'How would you secure cloud credentials in a pipeline?']],
  ['Containers and Kubernetes', ['What problem does Docker solve?', 'How do you debug a Kubernetes service that is not reachable?']],
  ['CI/CD and Monitoring', ['What makes a deployment pipeline production-ready?', 'Which alerts would you configure for a customer-facing API?']]
];

const behavioralPrompts = [
  'Tell me about a time you solved a difficult technical problem.',
  'Describe a time you received feedback and improved your work.',
  'Explain how you handle pressure during a production incident.',
  'Tell me about a time you worked with a teammate who had a different approach.'
];

const graduationTasks = [
  {
    key: 'linuxFundamentals',
    title: 'Linux Fundamentals',
    taskTitle: 'Provision and Configure a Secure Linux Server',
    objective: 'Set up, configure, secure, and automate a Linux server environment for DevOps adoption.',
    scenario: 'You are a Junior Linux Administrator preparing a safe server environment for developers at a startup.',
    passingScore: 80,
    unlocks: 'Next course',
    stages: [
      ['Linux Installation & Environment Setup', 'Submit uname -a, lsb_release -a, and ls / screenshots.'],
      ['File System and Directory Management', 'Create /peabsmart/projects, /peabsmart/backups, /peabsmart/scripts, and project_info.txt.'],
      ['User and Group Administration', 'Create devops_team, devuser1, devuser2, assign access, and verify permissions.'],
      ['Package Management', 'Install nginx, git, and htop, then verify installed versions.'],
      ['Service Configuration', 'Start and enable nginx, create /var/www/html/index.html, and verify in browser.'],
      ['Security Configuration', 'Enable UFW and allow only SSH and HTTP.'],
      ['Automation with Cron', 'Create /peabsmart/scripts/backup.sh and schedule daily backups with cron.']
    ],
    deliverables: [
      'Screenshots of all verification commands',
      'Backup script code',
      '200-word explanation of Linux in DevOps infrastructure',
      'Compressed project folder such as studentname_linux_task.zip'
    ],
    checklist: [
      'Linux installed',
      'Directory structure created',
      'Users and groups configured',
      'Permissions correctly applied',
      'Packages installed',
      'Nginx running',
      'Firewall configured'
    ]
  },
  {
    key: 'bashScripting',
    title: 'Bash Scripting',
    taskTitle: 'Build a DevOps Server Automation Toolkit',
    objective: 'Build Bash automation tools that monitor servers, automate backups, manage services, and generate reports.',
    scenario: 'Your DevOps team manages production Linux servers and needs reusable scripts for reliability work.',
    passingScore: 80,
    unlocks: 'Course 3 - Git & GitHub',
    stages: [
      ['Toolkit Structure', 'Create /peabsmart/scripts, /peabsmart/logs, and /peabsmart/backups.'],
      ['System Monitoring Tool', 'Build monitor.sh to log CPU, memory, disk, uptime, and nginx status.'],
      ['Automated Backup Tool', 'Build backup.sh to tar /peabsmart/projects into /peabsmart/backups.'],
      ['Service Recovery Tool', 'Build service_manager.sh to restart nginx when it fails and log the action.'],
      ['Daily System Report Generator', 'Build report.sh with uptime, CPU, memory, disk, and running services.'],
      ['Cron Automation', 'Schedule monitoring every 30 minutes, backups daily, and reports daily.'],
      ['Logging and Error Handling', 'Every script must produce logs and handle missing directories or failed operations.']
    ],
    deliverables: [
      'All scripts: monitor.sh, backup.sh, service_manager.sh, report.sh',
      'Screenshot of /peabsmart/scripts',
      'Screenshot of /peabsmart/logs',
      'Screenshot of crontab -l',
      'Example system_monitor.log output',
      '300-word explanation of how automation improves DevOps reliability'
    ],
    checklist: [
      'Script structure',
      'Monitoring automation',
      'Backup automation',
      'Service recovery',
      'Cron scheduling',
      'Logging'
    ]
  },
  {
    key: 'gitGithub',
    title: 'Git & GitHub',
    taskTitle: 'Manage a Real-World Software Project Using Git and GitHub',
    objective: 'Use Git and GitHub to manage a professional development workflow with commits, branches, pull requests, reviews, issues, releases, and basic automation.',
    scenario: 'You are a Junior Developer on a DevOps or software team. Your team needs a small project managed professionally through Git and GitHub from local setup through final release.',
    passingScore: 80,
    unlocks: 'Course 4 - Cloud Basics',
    stages: [
      ['Install and Configure Git', 'Install Git, verify git --version, and configure global username and email.'],
      ['Create a Local Git Project', 'Create git-github-graduation-project, initialize Git, add files, and complete the first commit.'],
      ['Create a GitHub Repository', 'Create a GitHub repository, connect the local remote, rename main, and push the project.'],
      ['Use Branching Workflow', 'Create and push main, feature/update-readme, feature/add-project-page, and bugfix/fix-project-error branches.'],
      ['Create Pull Requests and Perform Code Review', 'Open at least two pull requests with titles, descriptions, review comments, and successful merges.'],
      ['Resolve a Merge Conflict', 'Create and resolve a README.md conflict, then document the process in CONFLICT-RESOLUTION.md.'],
      ['Use GitHub Issues and Project Board', 'Create at least three issues, labels, and a project board with To Do, In Progress, and Done columns.'],
      ['Final Release and Advanced Git Feature', 'Create tag v1.0.0, publish a GitHub release, and demonstrate GitHub Actions, Pages, stash, rebase, revert, cherry-pick, or squashed commits.']
    ],
    deliverables: [
      'GitHub repository link',
      'Screenshot of Git version and Git configuration',
      'Screenshot of initialized repository and first commit',
      'Screenshot of GitHub repository homepage and successful push',
      'Screenshot of branch list and GitHub branches page',
      'Screenshots of pull request, review comment, and merged pull request',
      'CONFLICT-RESOLUTION.md with screenshot of conflict and resolution',
      'Screenshots of GitHub Issues and Project Board',
      'Screenshot of GitHub release and advanced Git/GitHub feature evidence'
    ],
    checklist: [
      'Git installed and configured',
      'Local repository initialized',
      'GitHub repository connected',
      'Branching workflow used',
      'Pull requests reviewed and merged',
      'Merge conflict resolved',
      'Issues and project board created',
      'Tagged release published',
      'Advanced Git or GitHub feature demonstrated'
    ]
  },
  {
    key: 'cloudBasics',
    title: 'Cloud Basics',
    taskTitle: 'Deploy a Production-Ready Linux Web Server in the Cloud',
    objective: 'Deploy and configure cloud infrastructure using AWS or Azure, including networking, SSH access, and a public web server.',
    scenario: 'You are a Junior Cloud Engineer helping a company move infrastructure from local servers to a cloud-hosted test web server.',
    passingScore: 80,
    unlocks: 'Course 5 - Infrastructure as Code',
    stages: [
      ['Create Cloud Account', 'Create a free-tier cloud account and submit a cloud dashboard screenshot.'],
      ['Launch Virtual Machine', 'Launch Ubuntu Server 22.04 with at least 1 vCPU, 1 GB RAM, public IP, and SSH access.'],
      ['Configure Firewall or Security Group', 'Allow SSH on port 22 from your IP and HTTP on port 80 from anywhere.'],
      ['Connect to the Server', 'Connect over SSH and submit a terminal login screenshot.'],
      ['Install and Configure Web Server', 'Install Nginx, start it, enable it at boot, and verify systemctl status nginx.'],
      ['Deploy Web Page', 'Create /var/www/html/index.html and verify the website through the server public IP.'],
      ['Bonus Challenge', 'Optionally add domain mapping, HTTPS with SSL, or automatic server updates.']
    ],
    deliverables: [
      'Screenshot of running cloud VM or instance dashboard',
      'Screenshot of firewall or security group rules showing ports 22 and 80',
      'Screenshot of SSH login',
      'Screenshot showing Nginx running',
      'Screenshot of website in browser through public IP',
      '250-word explanation of why cloud computing matters for DevOps infrastructure'
    ],
    checklist: [
      'Cloud account created',
      'VM instance deployed',
      'Firewall rules configured',
      'SSH access working',
      'Nginx installed',
      'Web page accessible'
    ]
  },
  {
    key: 'infrastructureAsCode',
    title: 'Infrastructure as Code',
    taskTitle: 'Automate Cloud Infrastructure Deployment Using Terraform',
    objective: 'Provision cloud infrastructure with Terraform configuration files so servers and web infrastructure can be recreated consistently.',
    scenario: 'You previously deployed a cloud web server manually. Your DevOps team now needs the same infrastructure deployed automatically with Terraform.',
    passingScore: 80,
    unlocks: 'Course 6 - Containerization',
    stages: [
      ['Install Terraform', 'Install Terraform locally and verify with terraform -v.'],
      ['Create Terraform Project Structure', 'Create terraform-devops-project with main.tf, variables.tf, outputs.tf, and README.md.'],
      ['Configure Cloud Provider', 'Configure AWS, Azure, or Google Cloud provider authentication and region settings.'],
      ['Provision Virtual Machine', 'Use terraform init, terraform plan, and terraform apply to create a Linux VM, public IP, and firewall rules for ports 22 and 80.'],
      ['Install Web Server Automatically', 'Use user_data, startup script, or provisioner to install and start Nginx during deployment.'],
      ['Output Server Public IP', 'Configure Terraform outputs for the public IP and verify the web server in a browser.'],
      ['Bonus Challenge', 'Optionally add multiple servers, a load balancer, Terraform modules, or remote state storage.']
    ],
    deliverables: [
      'Terraform project folder with main.tf, variables.tf, and outputs.tf',
      'Screenshot showing terraform init',
      'Screenshot showing terraform plan',
      'Screenshot showing terraform apply',
      'Screenshot showing running cloud instance',
      'Screenshot showing web server in browser',
      '250-word explanation of why Infrastructure as Code matters in DevOps'
    ],
    checklist: [
      'Terraform installed',
      'Terraform project created',
      'Cloud provider configured',
      'VM created with Terraform',
      'Web server installed automatically',
      'Server accessible via browser'
    ]
  },
  {
    key: 'dockerContainerization',
    title: 'Docker Containerization',
    taskTitle: 'Containerize and Deploy a Multi-Service Application Using Docker',
    objective: 'Build, run, and manage containerized applications using Docker, Dockerfiles, networking, and Docker Compose.',
    scenario: 'Developers have built a web application that behaves differently across servers. Your job is to containerize it so it runs consistently anywhere.',
    passingScore: 80,
    unlocks: 'Course 7 - Kubernetes',
    stages: [
      ['Install Docker', 'Install Docker, verify docker --version, and confirm Docker is running with docker info.'],
      ['Create Application Project Structure', 'Create docker-devops-project with app/index.html, Dockerfile, docker-compose.yml, and README.md.'],
      ['Create Dockerfile', 'Use an nginx base image, copy the app file, expose port 80, build devops-web, and verify with docker images.'],
      ['Run Docker Container', 'Run devops-web on localhost:8080, verify with docker ps, and show the app in a browser.'],
      ['Deploy Multi-Container Environment', 'Create docker-compose.yml with web and database containers, then run docker-compose up -d.'],
      ['Container Logs and Management', 'Demonstrate docker logs, docker stop, and docker start with screenshots.'],
      ['Bonus Challenge', 'Optionally add custom networks, persistent volumes, environment variables, or health checks.']
    ],
    deliverables: [
      'Dockerfile',
      'docker-compose.yml',
      'Screenshot showing Docker images',
      'Screenshot showing running containers',
      'Screenshot of application running at http://localhost:8080',
      'Screenshot showing Docker logs',
      '250-word explanation of why containers matter in DevOps'
    ],
    checklist: [
      'Docker installed',
      'Dockerfile created',
      'Image built successfully',
      'Container running',
      'Docker Compose environment created',
      'Application accessible'
    ]
  },
  {
    key: 'kubernetes',
    title: 'Kubernetes',
    taskTitle: 'Deploy and Manage a Containerized Application in a Kubernetes Cluster',
    objective: 'Deploy, manage, expose, and scale containerized applications using Kubernetes.',
    scenario: 'Your company has containerized its application with Docker and now needs it deployed to Kubernetes for scalability and reliability.',
    passingScore: 80,
    unlocks: 'Course 8 - CI/CD',
    stages: [
      ['Install Kubernetes Tools', 'Install kubectl and minikube, start a cluster, and verify nodes with kubectl get nodes.'],
      ['Deploy Application to Kubernetes', 'Create deployment.yaml with 2 replicas using the Docker image from Course 6.'],
      ['Verify Pods', 'Confirm at least 2 pods are running with kubectl get pods.'],
      ['Create Kubernetes Service', 'Create service.yaml as a NodePort service exposing port 80.'],
      ['Access Application', 'Use minikube service devops-service and submit a browser screenshot.'],
      ['Scale the Application', 'Scale the deployment to 4 replicas and verify 4 running pods.'],
      ['Bonus Challenge', 'Optionally add ConfigMaps, Secrets, Ingress, or rolling updates.']
    ],
    deliverables: [
      'deployment.yaml',
      'service.yaml',
      'Screenshot showing cluster nodes',
      'Screenshot showing running pods',
      'Screenshot showing services',
      'Screenshot showing scaled pods',
      'Screenshot of application running in browser',
      '300-word explanation of why Kubernetes matters for DevOps orchestration'
    ],
    checklist: [
      'Kubernetes installed',
      'Cluster running',
      'Deployment created',
      'Pods running',
      'Service created',
      'Application accessible',
      'Scaling working'
    ]
  },
  {
    key: 'cicdPipeline',
    title: 'CI/CD Pipeline',
    taskTitle: 'Build an Automated CI/CD Pipeline for a Containerized Application',
    objective: 'Build a CI/CD pipeline that pulls code, builds a Docker image, pushes to a registry, and deploys to Kubernetes.',
    scenario: 'Your company wants to stop manual deployments and automatically build and deploy a containerized app whenever code changes.',
    passingScore: 80,
    unlocks: 'Course 9 - Observability',
    stages: [
      ['Prepare GitHub Repository', 'Create or use devops-cicd-project with Dockerfile, deployment.yaml, service.yaml, README.md, and app files.'],
      ['Build Docker Image', 'Build and test the Docker image locally, then verify with docker images.'],
      ['Push Image to Container Registry', 'Push the image to Docker Hub, GHCR, ECR, or ACR and submit registry evidence.'],
      ['Create CI/CD Workflow File', 'Create deploy.yml, Jenkinsfile, or equivalent to checkout, build, push, and deploy.'],
      ['Deploy to Kubernetes Automatically', 'Use the pipeline to apply deployment.yaml and service.yaml to Kubernetes.'],
      ['Trigger Pipeline from Git Push', 'Modify app content, push changes, and show the pipeline running automatically.'],
      ['Verify Updated Application', 'Confirm the latest version is live in the browser after pipeline completion.'],
      ['Bonus Challenge', 'Optionally add tests, rollback strategy, branch environments, version tagging, or secrets management.']
    ],
    deliverables: [
      'GitHub repository link',
      'CI/CD workflow file or Jenkinsfile',
      'Screenshot showing Docker image in registry',
      'Screenshot of successful pipeline run',
      'Screenshot showing Kubernetes pods',
      'Screenshot showing Kubernetes services',
      'Screenshot of final deployed application in browser',
      '300-word explanation of why CI/CD matters in modern DevOps'
    ],
    checklist: [
      'Repository prepared correctly',
      'Docker image builds successfully',
      'Image pushed to registry',
      'CI/CD workflow created',
      'Pipeline runs automatically on push',
      'Application deployed to Kubernetes',
      'Updated application accessible'
    ]
  },
  {
    key: 'observability',
    title: 'Observability',
    taskTitle: 'Implement a Production Monitoring Stack Using Prometheus and Grafana',
    objective: 'Monitor infrastructure and applications with Prometheus, Node Exporter, Grafana dashboards, and alert rules.',
    scenario: 'Your production systems run on Kubernetes with automated deployments, and management requires monitoring and alerting for failures.',
    passingScore: 80,
    unlocks: 'Peabsmart Certified DevOps Engineer',
    stages: [
      ['Deploy Prometheus', 'Install Prometheus with Docker, Kubernetes, or Helm and monitor CPU, memory, disk, and node health.'],
      ['Deploy Node Exporter', 'Install Node Exporter and verify Linux server metrics are available in Prometheus.'],
      ['Deploy Grafana', 'Install Grafana, access it at localhost:3000, and confirm the default admin login.'],
      ['Connect Grafana to Prometheus', 'Configure Prometheus as the Grafana data source and verify the connection.'],
      ['Create Monitoring Dashboard', 'Create panels for CPU, memory, disk, pod health, and container metrics.'],
      ['Configure Alert Rules', 'Create alerts for high CPU, high memory, or pod failure.'],
      ['Bonus Challenge', 'Optionally add Alertmanager, Slack alerts, email alerts, or Kubernetes cluster monitoring.']
    ],
    deliverables: [
      'Screenshot showing Prometheus running',
      'Screenshot showing Node Exporter metrics',
      'Screenshot showing Grafana dashboard',
      'Screenshot showing Prometheus targets',
      'Screenshot showing alert rule configuration',
      'Monitoring architecture diagram',
      '300-word explanation of why observability matters for production DevOps'
    ],
    checklist: [
      'Prometheus deployed',
      'Node Exporter collecting metrics',
      'Grafana installed',
      'Prometheus connected to Grafana',
      'Dashboard created',
      'Alert rule configured'
    ]
  }
];

const emptyGraduationSubmission = {
  evidenceLink: '',
  repositoryLink: '',
  compressedFolderLink: '',
  screenshotsLink: '',
  explanation: '',
  notes: ''
};

const QUIZ_DURATION_MINUTES = 40;
const TAKE_HOME_DURATION_HOURS = 48;

const portfolioRubricFields = [
  ['projectQuality', 'Project Quality'],
  ['documentationClarity', 'Documentation Clarity'],
  ['githubProfessionalism', 'GitHub Professionalism'],
  ['technicalDepth', 'Technical Depth'],
  ['deploymentEvidence', 'Deployment Evidence']
];

const practicalRubricFields = [
  ['speed', 'Speed'],
  ['troubleshootingLogic', 'Troubleshooting Logic'],
  ['correctness', 'Correctness'],
  ['commandUsage', 'Command Usage'],
  ['solutionExplanation', 'Explanation']
];

const behavioralRubricFields = [
  ['clarity', 'Clarity'],
  ['ownership', 'Ownership'],
  ['teamwork', 'Teamwork'],
  ['incidentCommunication', 'Incident Communication']
];

const graduationTaskScoreFields = [
  ['linuxFundamentals', 'Linux Fundamentals'],
  ['bashScripting', 'Bash Scripting'],
  ['gitGithub', 'Git & GitHub'],
  ['cloudBasics', 'Cloud Basics'],
  ['infrastructureAsCode', 'Infrastructure as Code'],
  ['dockerContainerization', 'Docker Containerization'],
  ['kubernetes', 'Kubernetes'],
  ['cicdPipeline', 'CI/CD Pipeline'],
  ['observability', 'Observability']
];

const emptyPracticalSubmission = {
  rootCause: '',
  commandsUsed: '',
  fixApplied: '',
  evidenceLink: '',
  solutionExplanation: ''
};

const emptySystemDesign = {
  architectureDiagram: '',
  deploymentExplanation: '',
  scalingStrategy: '',
  monitoringDesign: ''
};

const emptyTakeHome = {
  repoUrl: '',
  architectureDiagram: '',
  screenshotsLink: '',
  deploymentExplanation: ''
};

const averageValues = (values) => {
  const numericValues = values.map(Number).filter((value) => Number.isFinite(value) && value > 0);
  if (!numericValues.length) return 0;
  return Math.round(numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length);
};

const getScoreValue = (profile, key) => {
  if (key === 'technicalQuizScore') {
    return Number(profile?.technicalQuizScore || 0);
  }
  if (key === 'portfolioScore') {
    return Number(profile?.portfolioScore || averageValues(Object.values(profile?.portfolioRubric || {})));
  }
  if (key === 'practicalChallengeScore') {
    return Number(profile?.practicalChallengeScore || averageValues(Object.values(profile?.practicalChallengeRubric || {})));
  }
  if (key === 'mockInterviewScore') {
    return Number(profile?.mockInterviewScore || averageValues(Object.values(profile?.behavioralRubric || {})));
  }
  if (key === 'linuxBashScore') {
    return Number(profile?.linuxBashScore || averageValues([
      profile?.graduationTaskScores?.linuxFundamentals,
      profile?.graduationTaskScores?.bashScripting
    ]));
  }
  if (key === 'gitGithubScore') {
    return Number(profile?.gitGithubScore || profile?.graduationTaskScores?.gitGithub || 0);
  }
  if (key === 'cloudIacScore') {
    return Number(profile?.cloudIacScore || averageValues([
      profile?.graduationTaskScores?.cloudBasics,
      profile?.graduationTaskScores?.infrastructureAsCode
    ]));
  }
  if (key === 'dockerKubernetesScore') {
    return Number(profile?.dockerKubernetesScore || averageValues([
      profile?.graduationTaskScores?.dockerContainerization,
      profile?.graduationTaskScores?.kubernetes
    ]));
  }
  if (key === 'cicdScore') {
    return Number(profile?.cicdScore || profile?.graduationTaskScores?.cicdPipeline || 0);
  }
  if (key === 'observabilityScore') {
    return Number(profile?.observabilityScore || profile?.graduationTaskScores?.observability || 0);
  }
  return Number(profile?.[key] || 0);
};

const formatRemainingTime = (milliseconds) => {
  if (milliseconds <= 0) return '00:00:00';
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

const isFilled = (value) => String(value || '').trim().length > 0;

const getCareerLaunchCompletion = (profile) => {
  const checks = [
    profile?.portfolioVerified || getScoreValue(profile, 'portfolioScore') > 0,
    profile?.technicalQuizSubmitted && getScoreValue(profile, 'technicalQuizScore') > 0,
    getScoreValue(profile, 'practicalChallengeScore') > 0,
    getScoreValue(profile, 'mockInterviewScore') > 0,
    getScoreValue(profile, 'systemDesignScore') > 0,
    getScoreValue(profile, 'takeHomeScore') > 0,
    Boolean(profile?.employerReportPublished)
  ];
  return {
    completed: checks.filter(Boolean).length,
    total: checks.length,
    isComplete: checks.every(Boolean)
  };
};

const calculateReadinessScore = (profile) => {
  const values = scoreFields
    .map((field) => getScoreValue(profile, field.key))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
};

const getJobLevel = (score) => {
  if (score >= 85) return 'Junior DevOps Engineer';
  if (score >= 70) return 'DevOps Internship Ready';
  if (score >= 50) return 'Final Preparation Needed';
  return 'Career Launch In Progress';
};

const slugify = (value) => (
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'candidate'
);

const CareerLaunchPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [profile, setProfile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [modules, setModules] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [portfolioForm, setPortfolioForm] = useState({
    githubProfile: '',
    portfolioLink: '',
    capstoneRepository: '',
    certificationId: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [workForm, setWorkForm] = useState({
    technicalQuizNotes: '',
    practicalChallengeNotes: '',
    mockInterviewNotes: '',
    systemDesignSubmission: '',
    takeHomeSubmission: '',
    behavioralResponses: ''
  });
  const [quizStartedAt, setQuizStartedAt] = useState(null);
  const [quizExpiresAt, setQuizExpiresAt] = useState(null);
  const [quizAutoSubmitting, setQuizAutoSubmitting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedChallenge, setSelectedChallenge] = useState(challengeCards[0][0]);
  const [practicalSubmission, setPracticalSubmission] = useState(emptyPracticalSubmission);
  const [selectedGraduationTask, setSelectedGraduationTask] = useState(graduationTasks[0].key);
  const [graduationTaskSubmissions, setGraduationTaskSubmissions] = useState({});
  const [interviewAnswers, setInterviewAnswers] = useState({});
  const [behavioralAnswers, setBehavioralAnswers] = useState({});
  const [systemDesignForm, setSystemDesignForm] = useState(emptySystemDesign);
  const [takeHomeForm, setTakeHomeForm] = useState(emptyTakeHome);
  const [takeHomeStartedAt, setTakeHomeStartedAt] = useState(null);
  const [takeHomeDueAt, setTakeHomeDueAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [reviewScores, setReviewScores] = useState({});

  const isReviewer = ['admin', 'instructor', 'teaching_assistant'].includes(user.role);

  const loadData = useCallback(async () => {
    try {
      const [profileData, progressData, modulesData] = await Promise.all([
        getCareerLaunchProfile(user.id),
        getUserProgress(user.id),
        getAllModules()
      ]);

      setProfile(profileData);
      setProgress(progressData);
      setModules(modulesData.sort((a, b) => a.order - b.order));

      if (profileData) {
        setPortfolioForm({
          githubProfile: profileData.githubProfile || '',
          portfolioLink: profileData.portfolioLink || '',
          capstoneRepository: profileData.capstoneRepository || '',
          certificationId: profileData.certificationId || ''
        });
        setWorkForm({
          technicalQuizNotes: profileData.technicalQuizNotes || '',
          practicalChallengeNotes: profileData.practicalChallengeNotes || '',
          mockInterviewNotes: profileData.mockInterviewNotes || '',
          systemDesignSubmission: profileData.systemDesignSubmission || '',
          takeHomeSubmission: profileData.takeHomeSubmission || '',
          behavioralResponses: profileData.behavioralResponses || ''
        });
        setQuizAnswers(profileData.technicalQuizAnswers || {});
        setQuizStartedAt(profileData.technicalQuizStartedAt || null);
        setQuizExpiresAt(profileData.technicalQuizExpiresAt || null);
        setSelectedChallenge(profileData.selectedChallenge || challengeCards[0][0]);
        setPracticalSubmission({ ...emptyPracticalSubmission, ...(profileData.practicalSubmission || {}) });
        setSelectedGraduationTask(profileData.selectedGraduationTask || graduationTasks[0].key);
        setGraduationTaskSubmissions(profileData.graduationTaskSubmissions || {});
        setInterviewAnswers(profileData.interviewAnswers || {});
        setBehavioralAnswers(profileData.behavioralAnswers || {});
        setSystemDesignForm({ ...emptySystemDesign, ...(profileData.systemDesign || {}) });
        setTakeHomeForm({ ...emptyTakeHome, ...(profileData.takeHome || {}) });
        setTakeHomeStartedAt(profileData.takeHomeStartedAt || null);
        setTakeHomeDueAt(profileData.takeHomeDueAt || null);
      }

      if (isReviewer) {
        const allProfiles = await getAllCareerLaunchProfiles();
        setProfiles(allProfiles);
      }
    } catch (error) {
      toast.error('Failed to load Career Launch');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isReviewer, user.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const isUnlocked = useMemo(() => {
    if (isReviewer) return true;
    if (progress?.careerLaunchEligible) return true;
    if (!modules.length) return false;

    const completed = progress?.completedModuleIds || [];
    const unlocked = progress?.unlockedModuleIds || [];
    const finalModule = modules[modules.length - 1];

    return completed.length >= modules.length || completed.includes(finalModule.id) || unlocked.includes(finalModule.id);
  }, [isReviewer, modules, progress]);

  const readinessScore = calculateReadinessScore(profile);
  const reportSlug = profile?.talentReportSlug || slugify(user.name || user.email || user.id);
  const reportUrl = `${window.location.origin}/talent/${reportSlug}`;
  const quizRemainingMs = quizExpiresAt ? new Date(quizExpiresAt).getTime() - now : 0;
  const quizLocked = Boolean(profile?.technicalQuizSubmitted || (quizExpiresAt && quizRemainingMs <= 0));
  const takeHomeRemainingMs = takeHomeDueAt ? new Date(takeHomeDueAt).getTime() - now : 0;
  const takeHomeLocked = Boolean(profile?.takeHomeSubmitted || (takeHomeDueAt && takeHomeRemainingMs <= 0));
  const careerLaunchCompletion = getCareerLaunchCompletion(profile);
  const activeGraduationTask = graduationTasks.find((task) => task.key === selectedGraduationTask) || graduationTasks[0];
  const activeGraduationSubmission = {
    ...emptyGraduationSubmission,
    ...(graduationTaskSubmissions[activeGraduationTask.key] || {})
  };

  const completionCards = [
    ['Portfolio Review', profile?.portfolioSubmitted ? 'Complete' : 'Pending', profile?.portfolioSubmitted],
    ['Technical Screening', profile?.technicalQuizSubmitted ? 'Submitted' : profile?.technicalQuizStartedAt ? 'Started' : 'Pending', profile?.technicalQuizSubmitted],
    ['Practical DevOps Challenge', profile?.practicalChallengeNotes ? 'Started' : 'Pending', profile?.practicalChallengeNotes],
    ['Graduation Tasks', profile?.graduationTaskSubmissions ? 'Started' : 'Pending', profile?.graduationTaskSubmissions],
    ['Mock Interview', profile?.mockInterviewNotes ? 'Started' : 'Pending', profile?.mockInterviewNotes],
    ['Take-Home Assignment', profile?.takeHomeSubmitted ? 'Submitted' : profile?.takeHomeStartedAt ? 'Started' : 'Pending', profile?.takeHomeSubmitted],
    ['System Design', profile?.systemDesignSubmission ? 'Submitted' : 'Pending', profile?.systemDesignSubmission],
    ['Behavioral Training', profile?.behavioralResponses ? 'Started' : 'Pending', profile?.behavioralResponses],
    ['Employer Report', profile?.employerReportPublished ? 'Published' : 'Pending', profile?.employerReportPublished]
  ];

  const savePortfolio = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      let resumeData = {};

      if (resumeFile) {
        const allowedTypes = [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(resumeFile.type) || resumeFile.size > 10 * 1024 * 1024) {
          toast.error('Upload a PDF or DOCX resume up to 10 MB.');
          return;
        }

        const storageRef = ref(storage, `career-launch/${user.id}/${Date.now()}_${resumeFile.name}`);
        await uploadBytes(storageRef, resumeFile);
        resumeData = {
          resumeName: resumeFile.name,
          resumeUrl: await getDownloadURL(storageRef)
        };
      }

      await saveCareerLaunchProfile(user.id, {
        ...portfolioForm,
        ...resumeData,
        studentName: user.name,
        studentEmail: user.email,
        portfolioSubmitted: true,
        status: 'portfolio_submitted'
      });

      toast.success('Portfolio submitted for review');
      setResumeFile(null);
      await loadData();
    } catch (error) {
      toast.error('Failed to save portfolio');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const saveWork = useCallback(async (updates, successMessage) => {
    setSubmitting(true);
    try {
      await saveCareerLaunchProfile(user.id, {
        ...updates,
        studentName: user.name,
        studentEmail: user.email
      });
      toast.success(successMessage);
      await loadData();
    } catch (error) {
      toast.error('Failed to save Career Launch work');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  }, [loadData, user.email, user.id, user.name]);

  const startQuiz = async () => {
    const startedAtDate = new Date();
    const startedAt = startedAtDate.toISOString();
    const expiresAt = new Date(startedAtDate.getTime() + QUIZ_DURATION_MINUTES * 60 * 1000).toISOString();
    setQuizStartedAt(startedAt);
    setQuizExpiresAt(expiresAt);
    await saveWork({
      technicalQuizStartedAt: startedAt,
      technicalQuizExpiresAt: expiresAt,
      technicalQuizStatus: 'in_progress'
    }, 'Technical quiz started');
  };

  const submitQuiz = useCallback(async () => {
    const mcqQuestions = quizQuestions
      .map((question, index) => ({ ...question, index }))
      .filter((question) => question.type === 'mcq');
    const correctAnswers = mcqQuestions.filter((question) => quizAnswers[question.index] === question.answer).length;
    const autoScore = Math.round((correctAnswers / mcqQuestions.length) * 100);

    await saveWork({
      technicalQuizAnswers: quizAnswers,
      technicalQuizAutoScore: autoScore,
      technicalQuizSubmitted: true,
      technicalQuizSubmittedAt: new Date().toISOString(),
      technicalQuizStatus: 'submitted',
      technicalQuizNotes: `${correctAnswers}/${mcqQuestions.length} multiple-choice answers correct. Short answers pending instructor review.`
    }, 'Technical quiz submitted');
  }, [quizAnswers, saveWork]);

  useEffect(() => {
    if (!quizExpiresAt || profile?.technicalQuizSubmitted || !quizStartedAt || quizAutoSubmitting) return;
    if (quizRemainingMs > 0) return;
    setQuizAutoSubmitting(true);
    submitQuiz();
  }, [profile?.technicalQuizSubmitted, quizAutoSubmitting, quizExpiresAt, quizRemainingMs, quizStartedAt, submitQuiz]);

  const saveInterviewRound = async () => {
    await saveWork({
      interviewAnswers,
      mockInterviewNotes: Object.entries(interviewAnswers)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n\n')
    }, 'Mock interview answers saved');
  };

  const saveBehavioralTraining = async () => {
    await saveWork({
      behavioralAnswers,
      behavioralResponses: Object.entries(behavioralAnswers)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n\n')
    }, 'Behavioral interview practice saved');
  };

  const savePracticalChallenge = async () => {
    await saveWork({
      selectedChallenge,
      practicalSubmission,
      practicalChallengeNotes: Object.entries(practicalSubmission)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n\n')
    }, 'Practical challenge saved');
  };

  const updateGraduationSubmission = (taskKey, fieldKey, value) => {
    setGraduationTaskSubmissions({
      ...graduationTaskSubmissions,
      [taskKey]: {
        ...emptyGraduationSubmission,
        ...(graduationTaskSubmissions[taskKey] || {}),
        [fieldKey]: value
      }
    });
  };

  const saveGraduationTask = async () => {
    await saveWork({
      selectedGraduationTask,
      graduationTaskSubmissions
    }, `${activeGraduationTask.title} graduation task saved`);
  };

  const saveSystemDesign = async () => {
    await saveWork({
      systemDesign: systemDesignForm,
      systemDesignSubmission: Object.entries(systemDesignForm)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n\n')
    }, 'System design saved');
  };

  const startTakeHome = async () => {
    const startedAtDate = new Date();
    const startedAt = startedAtDate.toISOString();
    const dueAt = new Date(startedAtDate.getTime() + TAKE_HOME_DURATION_HOURS * 60 * 60 * 1000).toISOString();
    setTakeHomeStartedAt(startedAt);
    setTakeHomeDueAt(dueAt);
    await saveWork({
      takeHomeStartedAt: startedAt,
      takeHomeDueAt: dueAt,
      takeHomeStatus: 'in_progress'
    }, '48-hour assignment started');
  };

  const saveTakeHome = async () => {
    await saveWork({
      takeHome: takeHomeForm,
      takeHomeSubmitted: true,
      takeHomeSubmittedAt: new Date().toISOString(),
      takeHomeStatus: 'submitted',
      takeHomeSubmission: Object.entries(takeHomeForm)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n\n')
    }, 'Take-home assignment submitted');
  };

  const updateReviewDraft = (studentId, key, value) => {
    setReviewScores({
      ...reviewScores,
      [studentId]: {
        ...(reviewScores[studentId] || {}),
        [key]: value
      }
    });
  };

  const updateReviewRubric = (studentId, rubricKey, fieldKey, value) => {
    const currentDraft = reviewScores[studentId] || {};
    setReviewScores({
      ...reviewScores,
      [studentId]: {
        ...currentDraft,
        [rubricKey]: {
          ...(currentDraft[rubricKey] || {}),
          [fieldKey]: Number(value)
        }
      }
    });
  };

  const saveReview = async (studentProfile) => {
    const scores = reviewScores[studentProfile.id] || {};
    const portfolioRubric = { ...(studentProfile.portfolioRubric || {}), ...(scores.portfolioRubric || {}) };
    const practicalChallengeRubric = { ...(studentProfile.practicalChallengeRubric || {}), ...(scores.practicalChallengeRubric || {}) };
    const behavioralRubric = { ...(studentProfile.behavioralRubric || {}), ...(scores.behavioralRubric || {}) };
    const graduationTaskScores = { ...(studentProfile.graduationTaskScores || {}), ...(scores.graduationTaskScores || {}) };
    const portfolioScore = scores.portfolioScore ?? (averageValues(Object.values(portfolioRubric)) || studentProfile.portfolioScore || 0);
    const practicalChallengeScore = scores.practicalChallengeScore ?? (averageValues(Object.values(practicalChallengeRubric)) || studentProfile.practicalChallengeScore || 0);
    const mockInterviewScore = scores.mockInterviewScore ?? (averageValues(Object.values(behavioralRubric)) || studentProfile.mockInterviewScore || 0);
    const linuxBashScore = scores.linuxBashScore ?? (averageValues([
      graduationTaskScores.linuxFundamentals,
      graduationTaskScores.bashScripting
    ]) || studentProfile.linuxBashScore || 0);
    const gitGithubScore = scores.gitGithubScore ?? (graduationTaskScores.gitGithub || studentProfile.gitGithubScore || 0);
    const cloudIacScore = scores.cloudIacScore ?? (averageValues([
      graduationTaskScores.cloudBasics,
      graduationTaskScores.infrastructureAsCode
    ]) || studentProfile.cloudIacScore || 0);
    const dockerKubernetesScore = scores.dockerKubernetesScore ?? (averageValues([
      graduationTaskScores.dockerContainerization,
      graduationTaskScores.kubernetes
    ]) || studentProfile.dockerKubernetesScore || 0);
    const cicdScore = scores.cicdScore ?? (graduationTaskScores.cicdPipeline || studentProfile.cicdScore || 0);
    const observabilityScore = scores.observabilityScore ?? (graduationTaskScores.observability || studentProfile.observabilityScore || 0);

    try {
      await reviewCareerLaunchProfile(studentProfile.id, {
        ...scores,
        portfolioRubric,
        practicalChallengeRubric,
        behavioralRubric,
        graduationTaskScores,
        portfolioScore,
        practicalChallengeScore,
        mockInterviewScore,
        linuxBashScore,
        gitGithubScore,
        cloudIacScore,
        dockerKubernetesScore,
        cicdScore,
        observabilityScore,
        reviewerId: user.id,
        reviewerName: user.name,
        finalRecommendation: scores.finalRecommendation || studentProfile.finalRecommendation || 'Continue preparing for entry-level DevOps roles.'
      });
      toast.success('Career Launch review saved');
      await loadData();
    } catch (error) {
      toast.error('Failed to save review');
      console.error(error);
    }
  };

  const publishReport = async (studentProfile) => {
    const score = calculateReadinessScore(studentProfile);
    const slug = studentProfile.talentReportSlug || slugify(studentProfile.studentName || studentProfile.studentEmail || studentProfile.id);

    try {
      await publishTalentReport(slug, {
        public: true,
        graduateName: studentProfile.studentName,
        studentEmail: studentProfile.studentEmail,
        certificationStatus: studentProfile.certificationId ? 'Verified' : 'Pending verification',
        certificationId: studentProfile.certificationId || '',
        githubProfile: studentProfile.githubProfile || '',
        portfolioLink: studentProfile.portfolioLink || '',
        capstoneRepository: studentProfile.capstoneRepository || '',
        skillsMastered: quizTopics,
        capstoneProjectSummary: studentProfile.systemDesignSubmission || studentProfile.takeHomeSubmission || 'Capstone details pending.',
        interviewSimulationScore: Number(studentProfile.mockInterviewScore || 0),
        practicalLabScore: Number(studentProfile.practicalChallengeScore || 0),
        technicalQuizScore: getScoreValue(studentProfile, 'technicalQuizScore'),
        communicationScore: Number(studentProfile.mockInterviewScore || 0),
        selectedChallenge: studentProfile.selectedChallenge || '',
        scoreBreakdown: scoreFields.map((field) => ({
          label: field.label,
          value: getScoreValue(studentProfile, field.key)
        })),
        portfolioRubric: studentProfile.portfolioRubric || {},
        practicalChallengeRubric: studentProfile.practicalChallengeRubric || {},
        behavioralRubric: studentProfile.behavioralRubric || {},
        graduationTasks: graduationTasks.map((task) => ({
          title: task.title,
          taskTitle: task.taskTitle,
          passingScore: task.passingScore,
          score: Number(studentProfile.graduationTaskScores?.[task.key] || 0),
          submitted: Boolean(studentProfile.graduationTaskSubmissions?.[task.key])
        })),
        systemDesign: studentProfile.systemDesign || {},
        takeHome: studentProfile.takeHome || {},
        verification: {
          portfolioVerified: Boolean(studentProfile.portfolioVerified || studentProfile.portfolioScore),
          certificationVerified: Boolean(studentProfile.certificationVerified || studentProfile.certificationId),
          careerLaunchCompleted: getCareerLaunchCompletion({ ...studentProfile, employerReportPublished: true }).isComplete
        },
        hiringReadinessRating: score,
        jobLevel: getJobLevel(score),
        finalRecommendation: studentProfile.finalRecommendation || 'Ready for Career Launch review.'
      });
      await reviewCareerLaunchProfile(studentProfile.id, {
        employerReportPublished: true,
        talentReportSlug: slug
      });
      toast.success('Employer report published');
      await loadData();
    } catch (error) {
      toast.error('Failed to publish employer report');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading Career Launch...</div>
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-[#0B0E14] px-6 py-16">
        <div className="max-w-3xl mx-auto bg-[#151923] border border-[#1E293B] rounded-md p-8 text-center">
          <Lock className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-3">Career Launch Locked</h1>
          <p className="text-[#94A3B8]">
            Career Launch opens when you complete the DevOps program or reach the final capstone phase.
          </p>
          <div className="mt-6">
            <Progress value={modules.length ? ((progress?.completedModuleIds?.length || 0) / modules.length) * 100 : 0} className="h-2" />
            <div className="text-sm text-[#64748B] mt-3">
              {(progress?.completedModuleIds?.length || 0)} of {modules.length} modules completed
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="career-launch-page" className="min-h-screen bg-[#0B0E14] pb-12">
      <div className="bg-[#151923] border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 text-[#00E5FF] font-medium mb-2">
                <BriefcaseBusiness className="w-5 h-5" />
                DevOps Job Readiness
              </div>
              <h1 className="text-4xl font-bold">Career Launch</h1>
              <p className="text-[#94A3B8] mt-2 max-w-3xl">
                Prepare for real DevOps hiring with portfolio review, screening tests, practical labs, mock interviews, and employer-ready reporting.
              </p>
            </div>
            <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-5 min-w-[240px]">
              <div className="text-sm text-[#94A3B8]">Hiring Readiness Score</div>
              <div className="text-4xl font-bold text-[#00E5FF] mt-1">{readinessScore}%</div>
              <div className="text-xs text-[#64748B] mt-1">{getJobLevel(readinessScore)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm whitespace-nowrap border ${
                activeTab === tab
                  ? 'bg-[#00E5FF] text-black border-[#00E5FF]'
                  : 'bg-[#151923] text-[#94A3B8] border-[#1E293B] hover:border-[#334155]'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'Hiring Readiness' && (
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 mb-8">
              {completionCards.map(([label, status, complete]) => (
                <div key={label} className="bg-[#151923] border border-[#1E293B] rounded-md p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#94A3B8]">{label}</span>
                    {complete ? <CheckCircle className="w-5 h-5 text-[#10B981]" /> : <Gauge className="w-5 h-5 text-[#F59E0B]" />}
                  </div>
                  <div className="text-lg font-semibold">{status}</div>
                </div>
              ))}
            </div>

            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
              <h2 className="text-2xl font-bold mb-4">Hiring Readiness Scorecard</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {scoreFields.map((field) => (
                  <div key={field.key}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#94A3B8]">{field.label}</span>
                      <span className="font-mono">{getScoreValue(profile, field.key)}%</span>
                    </div>
                    <Progress value={getScoreValue(profile, field.key)} className="h-2" />
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-[#0B0E14] border border-[#1E293B] rounded-md">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm text-[#94A3B8]">Career Launch Completion</div>
                    <div className="font-semibold">
                      {careerLaunchCompletion.completed} of {careerLaunchCompletion.total} graduation readiness checks complete
                    </div>
                  </div>
                  <div className={careerLaunchCompletion.isComplete ? 'text-[#10B981]' : 'text-[#F59E0B]'}>
                    {careerLaunchCompletion.isComplete ? 'Employer-ready' : 'In progress'}
                  </div>
                </div>
                <Progress value={(careerLaunchCompletion.completed / careerLaunchCompletion.total) * 100} className="h-2" />
              </div>
              <div className="mt-6 p-4 bg-[#0B0E14] border border-[#1E293B] rounded-md">
                <div className="text-sm text-[#94A3B8]">Final Recommendation</div>
                <div className="text-[#F8FAFC] mt-1">
                  {profile?.finalRecommendation || 'Complete Career Launch activities and wait for instructor review.'}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'Portfolio Review' && (
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={savePortfolio} className="lg:col-span-2 bg-[#151923] border border-[#1E293B] rounded-md p-6 space-y-4">
              <h2 className="text-2xl font-bold">Submit DevOps Portfolio</h2>
              <div>
                <Label>DevOps Resume</Label>
                <Input type="file" accept=".pdf,.docx" onChange={(event) => setResumeFile(event.target.files?.[0] || null)} className="mt-2 bg-[#0B0E14] border-[#1E293B]" />
              </div>
              {[
                ['githubProfile', 'GitHub Profile Link'],
                ['portfolioLink', 'Portfolio Link'],
                ['capstoneRepository', 'Capstone Project Repository'],
                ['certificationId', 'Certification ID']
              ].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    value={portfolioForm[key]}
                    onChange={(event) => setPortfolioForm({ ...portfolioForm, [key]: event.target.value })}
                    className="mt-2 bg-[#0B0E14] border-[#1E293B]"
                  />
                </div>
              ))}
              <Button type="submit" disabled={submitting} className="bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
                <Upload className="w-4 h-4 mr-2" />
                {submitting ? 'Saving...' : 'Submit Portfolio'}
              </Button>
            </form>

            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
              <h3 className="text-xl font-bold mb-4">Portfolio Score Output</h3>
              <ScoreLine label="Portfolio Review Score" value={profile?.portfolioScore} />
              <ScoreLine label="Resume Score" value={profile?.resumeScore} />
              <div className="border-t border-[#1E293B] pt-4 mt-4 space-y-3">
                {portfolioRubricFields.map(([key, label]) => (
                  <ScoreLine key={key} label={label} value={profile?.portfolioRubric?.[key]} />
                ))}
              </div>
              {profile?.resumeUrl && (
                <a href={profile.resumeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#00E5FF] mt-4 text-sm">
                  View submitted resume <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </section>
        )}

        {activeTab === 'Technical Quiz' && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 text-[#00E5FF] mb-2">
                  <ClipboardCheck className="w-6 h-6" />
                  <span className="font-medium">Technical Screening Quiz</span>
                </div>
                <h2 className="text-2xl font-bold">30-question DevOps readiness check</h2>
                <p className="text-[#94A3B8] mt-1">
                  Answer multiple-choice and short-answer questions across Linux, cloud, Terraform, Docker, Kubernetes, CI/CD, monitoring, and incident response.
                </p>
              </div>
              <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 min-w-[220px]">
                <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                  <Clock className="w-4 h-4" />
                  {quizStartedAt ? 'Time remaining' : 'Quiz duration'}
                </div>
                <div className="text-2xl font-bold mt-1">{quizStartedAt ? formatRemainingTime(quizRemainingMs) : `${QUIZ_DURATION_MINUTES} min`}</div>
                <div className="text-xs text-[#64748B] mt-1">
                  {profile?.technicalQuizSubmitted ? `Auto score: ${profile.technicalQuizAutoScore || 0}%` : quizLocked ? 'Time expired' : quizStartedAt ? 'Quiz in progress' : 'Not started'}
                </div>
              </div>
            </div>

            {!quizStartedAt ? (
              <Button onClick={startQuiz} disabled={submitting} className="bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
                <PlayCircle className="w-4 h-4 mr-2" />
                Start Quiz
              </Button>
            ) : (
              <div className="space-y-5">
                {quizQuestions.map((question, index) => (
                  <div key={`${question.topic}-${index}`} className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-xs text-[#00E5FF] uppercase tracking-wide">{question.topic}</span>
                      <span className="text-xs text-[#64748B]">Question {index + 1} of {quizQuestions.length}</span>
                    </div>
                    <div className="font-semibold mb-3">{question.question}</div>
                    {question.type === 'mcq' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {question.options.map((option) => (
                          <label key={option} className="flex items-center gap-3 border border-[#1E293B] rounded-md p-3 text-sm hover:border-[#334155]">
                            <input
                              type="radio"
                              name={`quiz-${index}`}
                              value={option}
                              checked={quizAnswers[index] === option}
                              disabled={quizLocked}
                              onChange={(event) => setQuizAnswers({ ...quizAnswers, [index]: event.target.value })}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <Textarea
                        value={quizAnswers[index] || ''}
                        disabled={quizLocked}
                        onChange={(event) => setQuizAnswers({ ...quizAnswers, [index]: event.target.value })}
                        className="bg-[#151923] border-[#1E293B] min-h-[120px]"
                        placeholder="Answer with commands, reasoning, tradeoffs, and evidence..."
                      />
                    )}
                  </div>
                ))}
                <Button onClick={submitQuiz} disabled={submitting || quizLocked} className="bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
                  <Send className="w-4 h-4 mr-2" />
                  {profile?.technicalQuizSubmitted ? 'Quiz Submitted' : quizLocked ? 'Time Expired' : 'Submit Quiz'}
                </Button>
              </div>
            )}
          </section>
        )}

        {activeTab === 'Practical Challenges' && (
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {challengeCards.map(([title, description]) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => setSelectedChallenge(title)}
                  className={`text-left bg-[#151923] border rounded-md p-5 hover:border-[#334155] ${
                    selectedChallenge === title ? 'border-[#00E5FF]' : 'border-[#1E293B]'
                  }`}
                >
                  <MonitorCheck className="w-6 h-6 text-[#00E5FF] mb-3" />
                  <h3 className="font-bold mb-2">{title}</h3>
                  <p className="text-sm text-[#94A3B8]">{description}</p>
                </button>
              ))}
            </div>
            <StructuredSection
              icon={<Code2 className="w-6 h-6" />}
              title="Practical Challenge Submission"
              description={`Selected challenge: ${selectedChallenge}. Submit the evidence an instructor needs to score troubleshooting readiness.`}
              helper="Scoring criteria: speed, troubleshooting logic, correctness, command usage, and explanation."
              fields={[
                ['rootCause', 'Root Cause'],
                ['commandsUsed', 'Commands Used'],
                ['fixApplied', 'Fix Applied'],
                ['evidenceLink', 'Evidence or Screenshots Link'],
                ['solutionExplanation', 'Explanation of Solution']
              ]}
              values={practicalSubmission}
              onChange={(key, value) => setPracticalSubmission({ ...practicalSubmission, [key]: value })}
              onSave={savePracticalChallenge}
            />
          </section>
        )}

        {activeTab === 'Graduation Tasks' && (
          <section className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {graduationTasks.map((task) => (
                <button
                  key={task.key}
                  type="button"
                  onClick={() => setSelectedGraduationTask(task.key)}
                  className={`text-left bg-[#151923] border rounded-md p-5 hover:border-[#334155] ${
                    selectedGraduationTask === task.key ? 'border-[#00E5FF]' : 'border-[#1E293B]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h3 className="font-bold text-lg">{task.title}</h3>
                    <span className="text-xs text-[#00E5FF]">{task.passingScore}% pass</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{task.taskTitle}</p>
                  <div className="text-xs text-[#64748B] mt-3">Unlocks: {task.unlocks}</div>
                </button>
              ))}
            </div>

            <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                  <BadgeCheck className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{activeGraduationTask.taskTitle}</h2>
                  <p className="text-[#94A3B8] mt-1">{activeGraduationTask.objective}</p>
                </div>
              </div>
              <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 text-sm text-[#94A3B8] mb-5">
                {activeGraduationTask.scenario}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <TaskList title="Task Stages" items={activeGraduationTask.stages.map(([title, description]) => `${title}: ${description}`)} />
                <TaskList title="Deliverables" items={activeGraduationTask.deliverables} />
              </div>

              <TaskList title="Instructor Verification Checklist" items={activeGraduationTask.checklist} compact />

              <div className="mt-6">
                <StructuredSection
                  fields={[
                    ['evidenceLink', 'Evidence Folder Link'],
                    ['repositoryLink', 'Repository Link'],
                    ['compressedFolderLink', 'Compressed Project Folder Link'],
                    ['screenshotsLink', 'Screenshots Link'],
                    ['explanation', 'Written Explanation'],
                    ['notes', 'Additional Notes']
                  ]}
                  values={activeGraduationSubmission}
                  onChange={(key, value) => updateGraduationSubmission(activeGraduationTask.key, key, value)}
                  onSave={saveGraduationTask}
                  buttonLabel={`Save ${activeGraduationTask.title} Submission`}
                />
              </div>
            </div>
          </section>
        )}

        {activeTab === 'Mock Interviews' && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">DevOps Interview Simulator</h2>
                <p className="text-[#94A3B8] mt-1">Practice round-based technical answers before instructor review.</p>
              </div>
            </div>
            <div className="space-y-5">
              {interviewRounds.map(([round, questions]) => (
                <div key={round} className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
                  <h3 className="font-bold text-[#00E5FF] mb-3">{round}</h3>
                  <div className="space-y-4">
                    {questions.map((question) => (
                      <div key={question}>
                        <Label>{question}</Label>
                        <Textarea
                          value={interviewAnswers[question] || ''}
                          onChange={(event) => setInterviewAnswers({ ...interviewAnswers, [question]: event.target.value })}
                          className="mt-2 bg-[#151923] border-[#1E293B] min-h-[110px]"
                          placeholder="Answer out loud, then write the polished version here..."
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={saveInterviewRound} disabled={submitting} className="mt-5 bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
              <Send className="w-4 h-4 mr-2" />
              Save Interview Practice
            </Button>
          </section>
        )}

        {activeTab === 'Take-Home Assignment' && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">48-Hour Take-Home DevOps Assignment</h2>
                  <p className="text-[#94A3B8] mt-1">Build a pipeline that pulls from GitHub, builds Docker, deploys to Kubernetes, and includes monitoring.</p>
                </div>
              </div>
              <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 min-w-[220px]">
                <div className="text-sm text-[#94A3B8]">Deadline</div>
                <div className="text-2xl font-bold mt-1">{takeHomeStartedAt ? formatRemainingTime(takeHomeRemainingMs) : '48 hours'}</div>
                <div className="text-xs text-[#64748B] mt-1">{profile?.takeHomeSubmitted ? 'Submitted' : takeHomeLocked ? 'Time expired' : takeHomeStartedAt ? 'In progress' : 'Not started'}</div>
              </div>
            </div>
            {!takeHomeStartedAt ? (
              <Button onClick={startTakeHome} disabled={submitting} className="bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
                <PlayCircle className="w-4 h-4 mr-2" />
                Start 48-Hour Assignment
              </Button>
            ) : (
              <StructuredSection
                fields={[
                  ['repoUrl', 'GitHub Repository'],
                  ['architectureDiagram', 'Architecture Diagram Link'],
                  ['screenshotsLink', 'Screenshots Link'],
                  ['deploymentExplanation', 'Deployment Explanation']
                ]}
                values={takeHomeForm}
                disabled={takeHomeLocked}
                onChange={(key, value) => setTakeHomeForm({ ...takeHomeForm, [key]: value })}
                onSave={saveTakeHome}
                buttonLabel={profile?.takeHomeSubmitted ? 'Assignment Submitted' : takeHomeLocked ? 'Time Expired' : 'Submit Take-Home Assignment'}
              />
            )}
          </section>
        )}

        {activeTab === 'System Design' && (
          <StructuredSection
            icon={<Award className="w-6 h-6" />}
            title="DevOps Architecture Challenge"
            description="Design a DevOps platform using GitHub, Docker, Kubernetes, Terraform, and monitoring tools."
            fields={[
              ['architectureDiagram', 'Architecture Diagram Link'],
              ['deploymentExplanation', 'Deployment Explanation'],
              ['scalingStrategy', 'Scaling Strategy'],
              ['monitoringDesign', 'Monitoring Design']
            ]}
            values={systemDesignForm}
            onChange={(key, value) => setSystemDesignForm({ ...systemDesignForm, [key]: value })}
            onSave={saveSystemDesign}
            helper="Include architecture diagram, deployment explanation, scaling strategy, and monitoring design."
          />
        )}

        {activeTab === 'Behavioral Training' && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
                <BadgeCheck className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Behavioral Interview Training</h2>
                <p className="text-[#94A3B8] mt-1">Prepare STAR-style answers for teamwork, pressure, feedback, and problem-solving questions.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {behavioralPrompts.map((prompt) => (
                <div key={prompt} className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
                  <Label>{prompt}</Label>
                  <Textarea
                    value={behavioralAnswers[prompt] || ''}
                    onChange={(event) => setBehavioralAnswers({ ...behavioralAnswers, [prompt]: event.target.value })}
                    className="mt-2 bg-[#151923] border-[#1E293B] min-h-[150px]"
                    placeholder="Situation, task, action, result..."
                  />
                </div>
              ))}
            </div>
            <Button onClick={saveBehavioralTraining} disabled={submitting} className="mt-5 bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
              <Send className="w-4 h-4 mr-2" />
              Save Behavioral Practice
            </Button>
          </section>
        )}

        {activeTab === 'Employer Report' && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <h2 className="text-2xl font-bold mb-4">Employer Candidate Report</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <InfoCard label="Graduate Name" value={user.name} />
              <InfoCard label="Certification Status" value={profile?.certificationId ? 'Verified ID submitted' : 'Pending'} />
              <InfoCard label="Hiring Readiness Rating" value={`${readinessScore}%`} />
            </div>
            <p className="text-[#94A3B8] mb-4">
              Once an instructor publishes your report, employers can view your verified profile at:
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:items-center bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
              <span className="text-[#00E5FF] break-all">{reportUrl}</span>
              {profile?.employerReportPublished && (
                <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#10B981]">
                  Open report <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </section>
        )}

        {isReviewer && (
          <section className="mt-10 bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <h2 className="text-2xl font-bold mb-4">Instructor Review Tools</h2>
            {profiles.length === 0 ? (
              <div className="text-[#94A3B8]">No Career Launch submissions yet.</div>
            ) : (
              <div className="space-y-4">
                {profiles.map((studentProfile) => (
                  <div key={studentProfile.id} className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold">{studentProfile.studentName || studentProfile.studentEmail || 'Student'}</h3>
                        <div className="text-sm text-[#94A3B8]">{studentProfile.studentEmail}</div>
                        <div className="text-sm text-[#00E5FF] mt-2">Current readiness: {calculateReadinessScore(studentProfile)}%</div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveReview(studentProfile)} className="bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
                          Save Scores
                        </Button>
                        <Button onClick={() => publishReport(studentProfile)} variant="outline" className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">
                          Publish Report
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      {scoreFields.map((field) => (
                        <div key={field.key}>
                          <Label>{field.label}</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            defaultValue={getScoreValue(studentProfile, field.key) || ''}
                            onChange={(event) => updateReviewDraft(studentProfile.id, field.key, Number(event.target.value))}
                            className="mt-2 bg-[#151923] border-[#1E293B]"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">
                      <RubricPanel
                        title="Portfolio Rubric"
                        fields={portfolioRubricFields}
                        values={studentProfile.portfolioRubric || {}}
                        onChange={(key, value) => updateReviewRubric(studentProfile.id, 'portfolioRubric', key, value)}
                      />
                      <RubricPanel
                        title="Practical Challenge Rubric"
                        fields={practicalRubricFields}
                        values={studentProfile.practicalChallengeRubric || {}}
                        onChange={(key, value) => updateReviewRubric(studentProfile.id, 'practicalChallengeRubric', key, value)}
                      />
                      <RubricPanel
                        title="Behavioral Rubric"
                        fields={behavioralRubricFields}
                        values={studentProfile.behavioralRubric || {}}
                        onChange={(key, value) => updateReviewRubric(studentProfile.id, 'behavioralRubric', key, value)}
                      />
                      <RubricPanel
                        title="Graduation Task Scores"
                        fields={graduationTaskScoreFields}
                        values={studentProfile.graduationTaskScores || {}}
                        onChange={(key, value) => updateReviewRubric(studentProfile.id, 'graduationTaskScores', key, value)}
                      />
                    </div>
                    <div className="mt-4">
                      <Label>Final Recommendation</Label>
                      <Textarea
                        defaultValue={studentProfile.finalRecommendation || ''}
                        onChange={(event) => updateReviewDraft(studentProfile.id, 'finalRecommendation', event.target.value)}
                        className="mt-2 bg-[#151923] border-[#1E293B]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

const ScoreLine = ({ label, value }) => (
  <div className="mb-4">
    <div className="flex justify-between text-sm mb-2">
      <span className="text-[#94A3B8]">{label}</span>
      <span className="font-mono">{Number(value || 0)}%</span>
    </div>
    <Progress value={Number(value || 0)} className="h-2" />
  </div>
);

const InfoCard = ({ label, value }) => (
  <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
    <div className="text-sm text-[#94A3B8]">{label}</div>
    <div className="font-semibold mt-1">{value || 'Pending'}</div>
  </div>
);

const RubricPanel = ({ title, fields, values, onChange }) => (
  <div className="bg-[#151923] border border-[#1E293B] rounded-md p-4">
    <h4 className="font-bold mb-3">{title}</h4>
    <div className="space-y-3">
      {fields.map(([key, label]) => (
        <div key={key}>
          <Label>{label}</Label>
          <Input
            type="number"
            min="0"
            max="100"
            defaultValue={values[key] || ''}
            onChange={(event) => onChange(key, event.target.value)}
            className="mt-2 bg-[#0B0E14] border-[#1E293B]"
          />
        </div>
      ))}
    </div>
  </div>
);

const TaskList = ({ title, items, compact }) => (
  <div className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
    <h3 className="font-bold mb-3">{title}</h3>
    <div className={compact ? 'flex flex-wrap gap-2' : 'space-y-3'}>
      {items.map((item) => (
        compact ? (
          <span key={item} className="px-3 py-1 rounded bg-[#00E5FF]/10 text-[#00E5FF] text-sm">{item}</span>
        ) : (
          <div key={item} className="text-sm text-[#94A3B8]">{item}</div>
        )
      ))}
    </div>
  </div>
);

const StructuredSection = ({ icon, title, description, fields, values, onChange, onSave, helper, disabled, buttonLabel = 'Save Submission' }) => (
  <section className={title ? 'bg-[#151923] border border-[#1E293B] rounded-md p-6' : ''}>
    {title && (
      <div className="flex items-start gap-4 mb-5">
        {icon && (
          <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
            {icon}
          </div>
        )}
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-[#94A3B8] mt-1">{description}</p>
        </div>
      </div>
    )}
    {helper && (
      <div className="mb-4 bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 text-sm text-[#94A3B8]">
        {helper}
      </div>
    )}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {fields.map(([key, label]) => (
        <div key={key}>
          <Label>{label}</Label>
          <Textarea
            value={values[key] || ''}
            disabled={disabled}
            onChange={(event) => onChange(key, event.target.value)}
            className="mt-2 bg-[#0B0E14] border-[#1E293B] min-h-[130px]"
            placeholder="Add the link, explanation, commands, evidence, or design notes here..."
          />
        </div>
      ))}
    </div>
    <Button onClick={onSave} disabled={disabled} className="mt-4 bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
      <Send className="w-4 h-4 mr-2" />
      {buttonLabel}
    </Button>
  </section>
);

const WorkSection = ({ icon, title, description, value, onChange, onSave, helper }) => (
  <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
    <div className="flex items-start gap-4 mb-5">
      <div className="w-12 h-12 rounded-md bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h2 className="text-2xl font-bold">{title}</h2>
        <p className="text-[#94A3B8] mt-1">{description}</p>
      </div>
    </div>
    {helper && (
      <div className="mb-4 bg-[#0B0E14] border border-[#1E293B] rounded-md p-4 text-sm text-[#94A3B8]">
        {helper}
      </div>
    )}
    <Textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="bg-[#0B0E14] border-[#1E293B] min-h-[180px]"
      placeholder="Write your answer, links, explanation, commands used, and evidence here..."
    />
    <Button onClick={onSave} className="mt-4 bg-[#00E5FF] text-black hover:bg-[#33EBFF]">
      <Send className="w-4 h-4 mr-2" />
      Save Submission
    </Button>
  </section>
);

export default CareerLaunchPage;
